import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext.js';

function AdminAddUserForm() {
    const navigate = useNavigate();
    const { token, user: adminUser } = useAuth();
    const [formData, setFormData] = useState({
        firstName: '', lastName: '', email: '', phoneNumber: '', password: '',
        state: '', city: '', profession: '', joiningDate: '', role: 'user',
        status: 'active', isHighlighted: false, assignedState: '',
        bloodGroup: '', allergies: '', medicalConditions: '', dateOfBirth: '',
        emergencyContactName: '', emergencyContactRelation: '', emergencyContactPhone: '',
        subscriptionStatus: 'inactive'
    });
    const [photoFile, setPhotoFile] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [message, setMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const fileInputRef = useRef(null);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handlePhotoChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            setPhotoFile(file);
            setImagePreview(URL.createObjectURL(file));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setMessage('Adding member...');

        const submissionData = new FormData();
        // Append all form data fields
        for (const key in formData) {
            if (key === 'isHighlighted') {
                submissionData.append(key, formData[key] ? '1' : '0');
            } else {
                submissionData.append(key, formData[key]);
            }
        }
        if (photoFile) {
            submissionData.append('profilePhoto', photoFile);
        }

        try {
            const response = await fetch(`https://healthcard.searchisend.com/health_api/add_user_admin.php`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: submissionData
            });
            const result = await response.json();
            if (!response.ok) throw new Error(result.message);
            setMessage('Member added successfully! Redirecting...');
            setTimeout(() => navigate('/portal-management-7a8b9c/manage-members'), 2000);
        } catch (error) {
            setMessage(`Error: ${error.message}`);
            setIsLoading(false);
        }
    };

    return (
        <div className="w-full">
            <h2 className="text-2xl font-semibold text-gray-700 mb-4">Add New Member</h2>
            <form onSubmit={handleSubmit} className="space-y-6 bg-gray-50 p-6 rounded-lg">
                <div className="flex items-start gap-6">
                    <div className="flex-shrink-0">
                        <img
                            src={imagePreview || `https://ui-avatars.com/api/?name=?&background=8b0000&color=fff&size=128`}
                            alt="Profile Preview"
                            className="w-32 h-32 rounded-lg object-cover border"
                        />
                    </div>
                    <div className="flex-grow">
                        <label className="block text-sm font-medium text-gray-700 mb-2">Profile Photo (JPG/PNG, max 2MB)</label>
                        <input type="file" accept="image/jpeg, image/png" onChange={handlePhotoChange} ref={fileInputRef} className="block w-full text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-violet-50 file:text-violet-700 hover:file:bg-violet-100" />
                    </div>
                </div>

                <h3 className="text-xl font-bold text-gray-800 mt-8 mb-4 border-b pb-2">Basic Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <input name="firstName" value={formData.firstName} onChange={handleChange} placeholder="First Name*" required className="p-2 border rounded" />
                    <input name="lastName" value={formData.lastName} onChange={handleChange} placeholder="Last Name" className="p-2 border rounded" />
                    <input name="email" type="email" value={formData.email} onChange={handleChange} placeholder="Email" className="p-2 border rounded" />
                    <input name="phoneNumber" value={formData.phoneNumber} onChange={handleChange} placeholder="Phone Number*" required className="p-2 border rounded" />
                    <input name="password" type="password" value={formData.password} onChange={handleChange} placeholder="Password*" required className="p-2 border rounded" />
                    <input name="profession" value={formData.profession} onChange={handleChange} placeholder="Profession / Job Title" className="p-2 border rounded" />
                    <input name="state" value={formData.state} onChange={handleChange} placeholder="State" className="p-2 border rounded" />
                    <input name="city" value={formData.city} onChange={handleChange} placeholder="City" className="p-2 border rounded" />
                    <div className="flex flex-col">
                        <label className="text-xs text-slate-500 mb-1">Joining Date</label>
                        <input type="date" name="joiningDate" value={formData.joiningDate} onChange={handleChange} className="p-2 border rounded text-gray-500" />
                    </div>
                </div>

                <h3 className="text-xl font-bold text-gray-800 mt-8 mb-4 border-b pb-2 text-brand-secondary">Medical Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex flex-col">
                        <label className="text-xs text-slate-500 mb-1">Date of Birth</label>
                        <input type="date" name="dateOfBirth" value={formData.dateOfBirth} onChange={handleChange} className="p-2 border rounded text-gray-500" />
                    </div>
                    <div className="flex flex-col">
                        <label className="text-xs text-slate-500 mb-1">Blood Group</label>
                        <select name="bloodGroup" value={formData.bloodGroup} onChange={handleChange} className="p-2 border rounded">
                            <option value="">Select Blood Group</option>
                            {['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'].map(bg => <option key={bg} value={bg}>{bg}</option>)}
                        </select>
                    </div>
                    <input name="allergies" value={formData.allergies} onChange={handleChange} placeholder="Allergies (comma separated)" className="p-2 border rounded" />
                    <input name="medicalConditions" value={formData.medicalConditions} onChange={handleChange} placeholder="Medical Conditions" className="p-2 border rounded" />
                </div>

                <h3 className="text-xl font-bold text-gray-800 mt-8 mb-4 border-b pb-2 text-brand-secondary">Emergency Contact</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <input name="emergencyContactName" value={formData.emergencyContactName} onChange={handleChange} placeholder="Contact Name" className="p-2 border rounded" />
                    <input name="emergencyContactRelation" value={formData.emergencyContactRelation} onChange={handleChange} placeholder="Relation (e.g., Spouse)" className="p-2 border rounded" />
                    <input name="emergencyContactPhone" value={formData.emergencyContactPhone} onChange={handleChange} placeholder="Contact Phone" className="p-2 border rounded" />
                </div>

                <h3 className="text-xl font-bold text-gray-800 mt-8 mb-4 border-b pb-2">Account Capabilities</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-slate-100 p-4 rounded-xl border border-slate-200">
                    <div className="flex flex-col">
                        <label className="text-xs text-slate-500 font-bold mb-1">Login Status</label>
                        <select name="status" value={formData.status} onChange={handleChange} className="p-2 border rounded bg-white">
                            <option value="active">Account Active</option>
                            <option value="inactive">Account Inactive</option>
                            <option value="pending_approval">Pending Approval</option>
                        </select>
                    </div>

                    <div className="flex flex-col">
                        <label className="text-xs text-slate-500 font-bold mb-1">Health Card Subscription</label>
                        <select name="subscriptionStatus" value={formData.subscriptionStatus} onChange={handleChange} className="p-2 border rounded text-emerald-700 bg-emerald-50 border-emerald-300 font-bold">
                            <option value="inactive">Subscription Inactive</option>
                            <option value="active">Subscription Active</option>
                        </select>
                    </div>

                    {adminUser?.role === 'superadmin' && (
                        <>
                            <div className="flex flex-col md:col-span-2">
                                <label className="text-xs text-indigo-500 font-bold mb-1">Admin Access Role</label>
                                <select name="role" value={formData.role} onChange={handleChange} className="p-2 border rounded bg-indigo-50 text-indigo-700 border-indigo-200 font-bold focus:ring-2 focus:ring-indigo-500">
                                    <option value="user">Standard User</option>
                                    <option value="admin">Regional Admin</option>
                                    <option value="superadmin">Superadmin</option>
                                </select>
                            </div>
                            {formData.role === 'admin' && (
                                <div className="flex flex-col md:col-span-2 mt-2">
                                    <input name="assignedState" value={formData.assignedState} onChange={handleChange} placeholder="Assigned State for Admin (e.g., Rajasthan)" className="p-2 border rounded focus:ring-2 focus:ring-indigo-500" />
                                </div>
                            )}
                        </>
                    )}
                </div>

                {adminUser?.role === 'superadmin' && (
                    <div className="border-t pt-4 flex items-center justify-between">
                        <label htmlFor="isHighlighted" className="text-sm font-medium text-gray-700">Highlight this member</label>
                        <label htmlFor="isHighlighted" className="relative inline-flex items-center cursor-pointer">
                            <input type="checkbox" id="isHighlighted" name="isHighlighted" className="sr-only peer" checked={formData.isHighlighted} onChange={handleChange} />
                            <div className="w-11 h-6 bg-gray-300 rounded-full peer peer-focus:ring-4 peer-focus:ring-red-300 peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-brand-primary"></div>
                        </label>
                    </div>
                )}

                <div className="flex justify-end gap-4">
                    <button type="button" onClick={() => navigate('/portal-management-7a8b9c/manage-members')} className="bg-gray-500 text-white font-bold py-2 px-4 rounded">Cancel</button>
                    <button type="submit" disabled={isLoading} className="bg-brand-primary text-white font-bold py-2 px-4 rounded disabled:bg-gray-400">
                        {isLoading ? 'Adding...' : 'Add Member'}
                    </button>
                </div>
                {message && <p className="text-center mt-4">{message}</p>}
            </form>
        </div>
    );
}

export default AdminAddUserForm;

